import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileDown, Loader2, Mail, Download, Check, History,
  Send, ChevronDown, ChevronUp, Sparkles, UserRound, Settings as SettingsIcon,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

/** Build a YYYY-MM string for N months ago (0 = current month). */
function monthBack(n) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const names = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return "hace un momento";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)} d`;
  return then.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
}

export default function ExportForAccountantDialog({ restaurantId }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(monthBack(1));   // last month by default
  const [savedEmail, setSavedEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [busy, setBusy] = useState(null);            // 'download' | 'email' | null
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [resending, setResending] = useState(null);  // month_key currently being resent
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadHistory = async () => {
    if (!restaurantId) return;
    setHistoryLoading(true);
    try {
      const r = await api.listFinanceExportLog(restaurantId, 24);
      setHistory(r.items || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !restaurantId) return;
    setEmailLoading(true);
    api.getAccountantEmail(restaurantId)
      .then(r => setSavedEmail(r.accountant_email || ""))
      .catch(() => {})
      .finally(() => setEmailLoading(false));
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, restaurantId]);

  const monthOptions = Array.from({ length: 12 }, (_, i) => monthBack(i));

  const onDownload = async () => {
    setBusy("download");
    try {
      const { blob, filename } = await api.downloadFinanceExport(restaurantId, month);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP descargado");
    } catch (e) {
      toast.error(e.message || "Error descargando export");
    } finally {
      setBusy(null);
    }
  };

  const onSendEmail = async () => {
    if (!savedEmail) {
      toast.error("Configura primero el email del contable en Ajustes");
      return;
    }
    setPreviewLoading(true);
    try {
      const p = await api.previewMonthlyExportEmail(restaurantId, { month, emailTo: savedEmail });
      setPreview(p);
    } catch (e) {
      toast.error(e.message || "Error generando la vista previa");
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmSend = async () => {
    setBusy("email");
    try {
      const cc = preview?.cc || [];
      const r = await api.sendFinanceExportEmail(restaurantId, month, savedEmail, cc);
      toast.success(
        cc.length
          ? `Enviado a ${r.sent_to} + CC (${cc.length})`
          : `Enviado a ${r.sent_to}`,
      );
      setPreview(null);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Error enviando email");
    } finally {
      setBusy(null);
    }
  };

  const onResend = async (entry) => {
    const to = (savedEmail || entry.sent_to || "").trim();
    if (!to) {
      toast.error("Configura primero el email del contable en Ajustes");
      return;
    }
    setResending(entry.month_key);
    try {
      const r = await api.sendFinanceExportEmail(restaurantId, entry.month_key, to);
      toast.success(`Reenviado ${r.month} a ${r.sent_to}`);
      await loadHistory();
    } catch (e) {
      toast.error(e.message || "Error reenviando");
    } finally {
      setResending(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="export-accountant-btn">
          <FileDown className="h-3.5 w-3.5 mr-1.5" />
          Exportar para contable
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="export-accountant-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5 text-primary" />
            Exportar para contable
          </DialogTitle>
          <DialogDescription>
            Genera un ZIP con las facturas, CSVs de gastos/ventas, resumen anual
            y P&L en PDF del mes seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label htmlFor="export-month" className="text-xs font-medium">
              Mes a exportar
            </label>
            <select
              id="export-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="export-month-select"
            >
              {monthOptions.map(ym => (
                <option key={ym} value={ym}>{monthLabel(ym)}</option>
              ))}
            </select>
          </div>

          {/* Email del contable — read-only, se edita en Ajustes */}
          <div
            className="rounded-lg border border-border bg-muted/40 p-3 flex items-center gap-3"
            data-testid="accountant-email-readonly"
          >
            <UserRound className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">
                Contable
              </div>
              {emailLoading ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Cargando…</span>
                </div>
              ) : savedEmail ? (
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-emerald-700" />
                  <span className="truncate">{savedEmail}</span>
                  <Check className="h-3 w-3 text-emerald-700 shrink-0" />
                </div>
              ) : (
                <div className="text-xs text-amber-700">
                  Sin email configurado
                </div>
              )}
            </div>
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="shrink-0"
              data-testid="accountant-email-edit-link"
            >
              <Link to="/app/settings?tab=profile">
                <SettingsIcon className="h-3.5 w-3.5 mr-1" />
                {savedEmail ? "Editar" : "Configurar"}
              </Link>
            </Button>
          </div>

          <div className="rounded-lg bg-muted/50 border border-border p-3 text-xs text-foreground space-y-1">
            <p className="font-semibold">El ZIP incluye:</p>
            <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
              <li>Carpeta <code className="text-[10px] bg-muted px-1 rounded">facturas/</code> con todos los PDFs/imágenes OCR del mes</li>
              <li><code className="text-[10px] bg-muted px-1 rounded">gastos.csv</code> con NIF, base, IVA, categoría (formato español)</li>
              <li><code className="text-[10px] bg-muted px-1 rounded">ventas.csv</code> con desglose diario por canal</li>
              <li><code className="text-[10px] bg-muted px-1 rounded">resumen_anual.csv</code> con los 12 meses del año + totales</li>
              <li><code className="text-[10px] bg-muted px-1 rounded">resumen.pdf</code> con P&L listo para imprimir</li>
            </ul>
          </div>

          {/* ── History (collapsible) ─────────────────────── */}
          <div className="rounded-lg border border-border overflow-hidden" data-testid="export-history-section">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-slate-50 transition"
              onClick={() => setHistoryOpen(v => !v)}
              data-testid="export-history-toggle"
            >
              <span className="flex items-center gap-2 text-foreground">
                <History className="h-4 w-4 text-muted-foreground" />
                Histórico de envíos
                {history.length > 0 && (
                  <span className="inline-flex items-center justify-center text-[10px] font-semibold min-w-[18px] h-[18px] rounded-full bg-accent text-foreground px-1.5">
                    {history.length}
                  </span>
                )}
              </span>
              {historyOpen
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {historyOpen && (
              <div className="border-t border-border max-h-56 overflow-y-auto bg-card"
                   data-testid="export-history-list">
                {historyLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : history.length === 0 ? (
                  <p className="text-center text-xs text-muted-foreground py-4 px-3">
                    Aún no se ha enviado ninguna exportación. El primer envío aparecerá aquí.
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {history.map((h) => {
                      const isAuto = h.source !== "manual";
                      const Icon = isAuto ? Sparkles : UserRound;
                      return (
                        <li key={`${h.month_key}-${h.sent_at}`}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50"
                            data-testid={`export-history-row-${h.month_key}`}>
                          <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full shrink-0 ${
                            isAuto ? "bg-indigo-50 text-indigo-600" : "bg-muted text-muted-foreground"
                          }`}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium text-foreground">
                                {monthLabel(h.month_key)}
                              </span>
                              <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                isAuto ? "bg-indigo-50 text-indigo-700" : "bg-muted text-muted-foreground"
                              }`}>
                                {isAuto ? "Auto" : "Manual"}
                              </span>
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              a <b className="font-medium text-foreground">{h.sent_to}</b>
                              <span className="mx-1">·</span>
                              {formatRelative(h.sent_at)}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-slate-900 shrink-0"
                            onClick={() => onResend(h)}
                            disabled={resending === h.month_key || !savedEmail}
                            data-testid={`export-history-resend-${h.month_key}`}
                          >
                            {resending === h.month_key
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <><Send className="h-3 w-3 mr-1" /> Reenviar</>}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onDownload}
            disabled={!!busy}
            data-testid="export-download-btn"
          >
            {busy === "download"
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Descargar ZIP
          </Button>
          <Button
            onClick={onSendEmail}
            disabled={!!busy || !savedEmail || previewLoading}
            data-testid="export-send-email-btn"
          >
            {previewLoading
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Mail className="h-3.5 w-3.5 mr-1.5" />}
            {previewLoading ? "Generando vista previa…" : "Enviar al contable"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Preview + confirm dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => !busy && !v && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="export-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-violet-600" />
              Vista previa · Exportación mensual
            </DialogTitle>
            <DialogDescription>
              Revisa el email antes de enviarlo. Podrás editar el destinatario en Ajustes si no es correcto.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="rounded-lg border bg-muted/50 overflow-hidden" data-testid="export-preview-content">
              <div className="bg-card px-3 py-2 border-b text-[12px] space-y-0.5">
                <div><span className="text-muted-foreground">Para:</span> <b>{preview.to_email}</b></div>
                {(preview.cc || []).length > 0 && (
                  <div><span className="text-muted-foreground">CC:</span> <b>{(preview.cc || []).join(", ")}</b></div>
                )}
                <div><span className="text-muted-foreground">Asunto:</span> <b>{preview.subject}</b></div>
                <div className="text-[11px] text-muted-foreground">
                  Adjunto: {preview.filename} ({Math.round((preview.attachment_size_bytes || 0) / 1024)} KB) ·
                  {" "}{preview.invoice_count || 0} facturas · {preview.expense_count || 0} gastos
                </div>
              </div>
              <div
                className="bg-card p-3 max-h-72 overflow-auto text-sm"
                data-testid="export-preview-html"
                dangerouslySetInnerHTML={{ __html: preview.html }}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreview(null)} disabled={!!busy}>
              Cancelar
            </Button>
            <Button
              onClick={confirmSend}
              disabled={!!busy}
              data-testid="export-preview-confirm"
            >
              {busy === "email"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Mail className="h-3.5 w-3.5 mr-1.5" />}
              Enviar ahora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
